/**
 * Tab8_Debrief.jsx -- Post-Exercise After Action Review (AAR)
 * PSP/CRE Tool v3 -- Session B
 *
 * Read-only tab. Reads exercise data from localStorage (session history,
 * daily log, debrief snapshots). Renders:
 *   1. Exercise summary metrics
 *   2. Decision timeline (CUOPS/FUOPS planning windows, 3A scoring)
 *   3. Projected vs reported casualty chart (inline SVG)
 *   4. Decision audit table
 *   5. PDF AAR export
 *
 * Author: Dr. Tatar Radu MD
 * Version: 1.0.0 (Session B, April 2026)
 */

import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import ExpandMoreIcon    from '@mui/icons-material/ExpandMore';
import ArrowBackIcon     from '@mui/icons-material/ArrowBack';
import TimelineIcon      from '@mui/icons-material/Timeline';
import AssessmentIcon    from '@mui/icons-material/Assessment';
import ShowChartIcon     from '@mui/icons-material/ShowChart';
import GetAppIcon        from '@mui/icons-material/GetApp';
import CheckCircleIcon   from '@mui/icons-material/CheckCircle';
import WarningAmberIcon  from '@mui/icons-material/WarningAmber';
import ErrorIcon         from '@mui/icons-material/Error';
import SchoolIcon        from '@mui/icons-material/School';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import GroupsIcon        from '@mui/icons-material/Groups';
import RefreshIcon       from '@mui/icons-material/Refresh';

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

import { useTranslation } from 'react-i18next';
import { lsRead } from '../utils/lsStore.js';
import { getBaseline as getCalculatedBaseline } from '../services/calculationService.js';
import { useUserRole }    from '../context/UserRoleContext';
import { exerciseService } from '../services/exerciseService';
import { operationPeriodLabel, dayLabelZulu, instantOfDay, formatZulu, DEFAULT_START_TIME_Z } from '../constants/exerciseDates';

// ============================================================================
// CONSTANTS
// ============================================================================

const STORAGE_KEYS = {
  SESSION_HISTORY:   'psp_tab7_session_history',
  DAILY_LOG:         'psp_tab7_daily_log',
  DEBRIEF_SNAPSHOTS: 'psp_tab8_debrief_snapshots',
  APPROVED_PLAN:     'psp_approved_plan',
};

// Human-readable labels for inject IDs stored in history inject_metas
const INJECT_DISPLAY = {
  mroe:          { label: 'MRoE Change', color: 'warning' },
  civ:           { label: 'Civilian Casualties', color: 'error' },
  mascal:        { label: 'MASCAL Surge', color: 'error' },
  medevac:       { label: 'MEDEVAC Loss', color: 'error' },
  mtf:           { label: 'MTF Displacement', color: 'warning' },
  loc:           { label: 'LOC Interdiction', color: 'warning' },
  r2e:           { label: 'R2E Loss', color: 'error' },
  counterattack: { label: 'Enemy Counterattack', color: 'error' },
  weather:       { label: 'Weather Change', color: 'info' },
  convoy:        { label: 'Convoy Ambush', color: 'warning' },
};

// Decision option labels
const DECISION_LABELS = { A: 'Option A', B: 'Option B', C: 'Option C', D: 'Option D' };

// Colour helpers for threat/action/confidence chips
function threatColor(v) {
  if (v === 'LIFE' || v === 'BOTH') return 'error';
  if (v === 'MISSION') return 'warning';
  return 'default';
}
function actionColor(v) {
  if (v === 'EVACUATE') return 'error';
  if (v === 'PREPOSITION') return 'warning';
  return 'default';
}
function confidenceColor(v) {
  if (v === 'HIGH') return 'success';
  if (v === 'MEDIUM') return 'warning';
  if (v === 'LOW') return 'error';
  return 'default';
}

const PLANNING_WINDOW_META = {
  ASSESS:   { label: 'ASSESS',   muiColor: 'secondary', hex: '#9c27b0' },
  ORIENT:   { label: 'ORIENT',   muiColor: 'info',      hex: '#1976d2' },
  REVIEW:   { label: 'REVIEW',   muiColor: 'success',   hex: '#388e3c' },
  CONFIRM:  { label: 'CONFIRM',  muiColor: 'warning',   hex: '#f57c00' },
  APPROVE:  { label: 'APPROVE',  muiColor: 'error',     hex: '#d32f2f' },
  GUIDANCE: { label: 'GUIDANCE', muiColor: 'default',   hex: '#616161' },
};

const SCORE_META = {
  PROACTIVE: { label: 'PROACTIVE', muiColor: 'success', icon: CheckCircleIcon },
  TIMELY:    { label: 'TIMELY',    muiColor: 'warning', icon: WarningAmberIcon },
  REACTIVE:  { label: 'REACTIVE', muiColor: 'error',   icon: WarningAmberIcon },
  LATE:      { label: 'LATE',      muiColor: 'error',   icon: ErrorIcon },
};

// SVG chart dimensions
const CHART = {
  W:  760,
  H:  240,
  PAD_LEFT:   52,
  PAD_RIGHT:  24,
  PAD_TOP:    20,
  PAD_BOTTOM: 40,
};

// ============================================================================
// HELPERS
// ============================================================================

function fmtTs(iso) {
  if (!iso) return '--';
  const d = new Date(iso);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${d.toLocaleDateString()} ${hh}:${mm}`;
}

function fmtNum(v) {
  if (v == null || Number.isNaN(Number(v))) return '--';
  return Number(v).toLocaleString();
}

function fmtDelta(v) {
  if (v == null || Number.isNaN(Number(v))) return '--';
  const n = Math.round(Number(v));
  return n > 0 ? `+${n}` : String(n);
}

function score3A(entry) {
  const w    = entry.planning_window || 'ORIENT';
  const type = entry.type || 'INJECT';
  if (type === 'COUNTERMEASURE') {
    if (['REVIEW', 'CONFIRM', 'APPROVE', 'GUIDANCE'].includes(w)) return 'PROACTIVE';
    if (w === 'ORIENT') return 'TIMELY';
    return 'REACTIVE';
  }
  // INJECT type
  if (['REVIEW', 'CONFIRM'].includes(w)) return 'PROACTIVE';
  if (w === 'ORIENT') return 'TIMELY';
  if (['APPROVE', 'GUIDANCE'].includes(w)) return 'LATE';
  return 'REACTIVE'; // ASSESS
}

/** Extract by_day total_casualties array (length = duration) */
function extractDailySeries(byDay) {
  if (!Array.isArray(byDay) || byDay.length === 0) return [];
  return byDay.map(d => ({
    day:    d.day,
    total:  (d.totals?.total_casualties) ?? (
      (d.totals?.kia ?? 0) + (d.totals?.dow ?? 0) +
      (d.totals?.wia ?? 0) + (d.totals?.dnbi ?? 0)
    ),
  }));
}

/** Map daily log { [day]: { kia, wia, dnbi } } to series */
function extractActualSeries(dailyLog) {
  return Object.entries(dailyLog || {})
    .map(([dayStr, v]) => ({
      day:   Number(dayStr),
      total: (Number(v.kia) || 0) + (Number(v.wia) || 0) + (Number(v.dnbi) || 0),
    }))
    .filter(x => x.day > 0)
    .sort((a, b) => a.day - b.day);
}

/** Build SVG polyline points string from [{day, total}] given scales */
function buildPolylinePoints(series, xScale, yScale, chartH, padLeft, padTop) {
  return series
    .map(pt => {
      const x = padLeft + (pt.day - 1) * xScale;
      const y = padTop + chartH - pt.total * yScale;
      return `${x},${y}`;
    })
    .join(' ');
}

// ============================================================================
// SVG CHART COMPONENT
// ============================================================================

function CasualtyChart({ baselineSeries, latestSeries, actualSeries, maxDay, startDate = '', startTimeZ = DEFAULT_START_TIME_Z }) {
  const allVals = [
    ...baselineSeries.map(d => d.total),
    ...latestSeries.map(d => d.total),
    ...actualSeries.map(d => d.total),
    1,
  ];
  const maxVal  = Math.max(...allVals);
  const plotW   = CHART.W - CHART.PAD_LEFT - CHART.PAD_RIGHT;
  const plotH   = CHART.H - CHART.PAD_TOP  - CHART.PAD_BOTTOM;
  const xScale  = maxDay > 1 ? plotW / (maxDay - 1) : plotW;
  const yScale  = maxVal > 0 ? plotH / maxVal : 1;

  const yTicks = [0, 0.25, 0.5, 0.75, 1.0].map(f => ({
    val: Math.round(maxVal * f),
    y:   CHART.PAD_TOP + plotH - Math.round(maxVal * f) * yScale,
  }));
  const xTicks = Array.from({ length: maxDay }, (_, i) => i + 1).filter(
    d => maxDay <= 14 || d === 1 || d % 5 === 0 || d === maxDay
  );

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <svg
        viewBox={`0 0 ${CHART.W} ${CHART.H}`}
        width="100%"
        style={{ maxWidth: CHART.W, display: 'block' }}
        aria-label="Casualty projection chart"
      >
        {/* Grid lines */}
        {yTicks.map(tick => (
          <line
            key={tick.val}
            x1={CHART.PAD_LEFT}
            y1={tick.y}
            x2={CHART.W - CHART.PAD_RIGHT}
            y2={tick.y}
            stroke="#e0e0e0"
            strokeWidth="1"
          />
        ))}

        {/* Y-axis labels */}
        {yTicks.map(tick => (
          <text
            key={tick.val}
            x={CHART.PAD_LEFT - 6}
            y={tick.y + 4}
            textAnchor="end"
            fontSize="10"
            fill="#757575"
          >
            {fmtNum(tick.val)}
          </text>
        ))}

        {/* X-axis labels */}
        {xTicks.map(day => (
          <text
            key={day}
            x={CHART.PAD_LEFT + (day - 1) * xScale}
            y={CHART.PAD_TOP + plotH + 16}
            textAnchor="middle"
            fontSize="10"
            fill="#757575"
          >
            {`D+${day}`}
            {startDate && (() => {
              try {
                const z = formatZulu(instantOfDay(startDate, startTimeZ, day)).split(' ');   // R2-09: date and Zulu hour under the day
                return <tspan x={CHART.PAD_LEFT + (day - 1) * xScale} dy="11">{`${z[0]} ${z[1]}`}</tspan>;
              } catch (_e) { return null; }
            })()}
          </text>
        ))}

        {/* Y-axis label */}
        <text
          x={10}
          y={CHART.PAD_TOP + plotH / 2}
          textAnchor="middle"
          fontSize="10"
          fill="#9e9e9e"
          transform={`rotate(-90, 10, ${CHART.PAD_TOP + plotH / 2})`}
        >
          Cas/day
        </text>

        {/* Baseline trace (blue, solid) */}
        {baselineSeries.length > 1 && (
          <polyline
            points={buildPolylinePoints(
              baselineSeries, xScale, yScale, plotH, CHART.PAD_LEFT, CHART.PAD_TOP
            )}
            fill="none"
            stroke="#1976d2"
            strokeWidth="2"
          />
        )}

        {/* Latest inject snapshot trace (orange, dashed) */}
        {latestSeries.length > 1 && (
          <polyline
            points={buildPolylinePoints(
              latestSeries, xScale, yScale, plotH, CHART.PAD_LEFT, CHART.PAD_TOP
            )}
            fill="none"
            stroke="#f57c00"
            strokeWidth="2"
            strokeDasharray="6 3"
          />
        )}

        {/* Actual reported dots (green) */}
        {actualSeries.map(pt => (
          <circle
            key={pt.day}
            cx={CHART.PAD_LEFT + (pt.day - 1) * xScale}
            cy={CHART.PAD_TOP + plotH - pt.total * yScale}
            r={4}
            fill="#388e3c"
          />
        ))}

        {/* Axes */}
        <line
          x1={CHART.PAD_LEFT} y1={CHART.PAD_TOP}
          x2={CHART.PAD_LEFT} y2={CHART.PAD_TOP + plotH}
          stroke="#bdbdbd" strokeWidth="1"
        />
        <line
          x1={CHART.PAD_LEFT} y1={CHART.PAD_TOP + plotH}
          x2={CHART.W - CHART.PAD_RIGHT} y2={CHART.PAD_TOP + plotH}
          stroke="#bdbdbd" strokeWidth="1"
        />
      </svg>

      {/* Legend */}
      <Box sx={{ display: 'flex', gap: 3, mt: 1, flexWrap: 'wrap', pl: `${CHART.PAD_LEFT}px` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 24, height: 2, bgcolor: '#1976d2' }} />
          <Typography variant="caption" color="text.secondary">Baseline plan</Typography>
        </Box>
        {latestSeries.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 24, height: 2, bgcolor: '#f57c00', borderTop: '2px dashed #f57c00' }} />
            <Typography variant="caption" color="text.secondary">Post-inject projection</Typography>
          </Box>
        )}
        {actualSeries.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#388e3c' }}
            />
            <Typography variant="caption" color="text.secondary">Reported actual</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

// ============================================================================
// COMPONENT
// ============================================================================

const Tab8Debrief = forwardRef(function Tab8Debrief({ scenario, onNavigate, onComplete }, ref) {
  const { t } = useTranslation();

  // -- User role context (trainer vs student) --------------------------------
  const { userRole } = useUserRole();
  const isTrainer = userRole === 'TRAINER';

  // -- Imperative handle (read-only tab, no state to flush) ------------------
  useImperativeHandle(ref, () => ({
    flushState: () => {},
  }));

  // ── Cross-student Decision Analysis (trainer only) ────────────────────────
  const [exercises,         setExercises]         = useState([]);
  const [selectedExId,      setSelectedExId]       = useState('');
  const [injectDecisions,   setInjectDecisions]    = useState([]);
  const [aggregateLoading,  setAggregateLoading]   = useState(false);
  const [aggregateError,    setAggregateError]     = useState(null);
  const [aggExpanded,       setAggExpanded]        = useState({});

  // Load exercise list — called on mount and on manual refresh
  const loadExerciseList = useCallback(() => {
    if (!isTrainer) return;
    exerciseService.getExercises()
      .then(list => setExercises(Array.isArray(list) ? list : []))
      .catch(() => {});
  }, [isTrainer]);

  useEffect(() => { loadExerciseList(); }, [loadExerciseList]);

  // Load inject decisions when an exercise is selected
  const loadDecisions = useCallback(async (exId) => {
    if (!exId) { setInjectDecisions([]); return; }
    setAggregateLoading(true);
    setAggregateError(null);
    try {
      const rows = await exerciseService.getExerciseInjectDecisions(exId);
      setInjectDecisions(rows);
      setAggExpanded({});
    } catch (err) {
      setAggregateError(err.message || 'Failed to load decisions.');
      setInjectDecisions([]);
    } finally {
      setAggregateLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDecisions(selectedExId);
  }, [selectedExId, loadDecisions]);

  // Aggregate: group by inject_id, count decisions per option
  const aggregated = useMemo(() => {
    if (!injectDecisions.length) return [];
    const map = {};
    injectDecisions.forEach(d => {
      if (!map[d.inject_id]) {
        map[d.inject_id] = {
          inject_id:    d.inject_id,
          inject_label: d.inject_label || d.inject_id,
          decisions:    [],
        };
      }
      map[d.inject_id].decisions.push(d);
    });
    return Object.values(map).map(group => {
      const total = group.decisions.length;
      const decisionCounts = {};
      const studentsByDecision = {};
      const threatCounts = {};
      const actionCounts = {};
      const confidenceCounts = {};
      group.decisions.forEach(d => {
        const dec = d.decision || '?';
        decisionCounts[dec]  = (decisionCounts[dec] || 0) + 1;
        studentsByDecision[dec] = [...(studentsByDecision[dec] || []), d.student_name];
        if (d.threat_class) threatCounts[d.threat_class] = (threatCounts[d.threat_class] || 0) + 1;
        if (d.rec_action)   actionCounts[d.rec_action]   = (actionCounts[d.rec_action]   || 0) + 1;
        if (d.confidence)   confidenceCounts[d.confidence] = (confidenceCounts[d.confidence] || 0) + 1;
      });
      return { ...group, total, decisionCounts, studentsByDecision, threatCounts, actionCounts, confidenceCounts };
    // Sort by inject display order
    }).sort((a, b) => {
      const ORDER = ['mroe','civ','mascal','medevac','mtf','loc','r2e','counterattack','weather','convoy'];
      return (ORDER.indexOf(a.inject_id) ?? 99) - (ORDER.indexOf(b.inject_id) ?? 99);
    });
  }, [injectDecisions]);

  // -- Data loading (lazy, from localStorage) --------------------------------
  const sessionHistory = useMemo(
    () => lsRead(STORAGE_KEYS.SESSION_HISTORY) || [],
    []
  );
  const dailyLog = useMemo(
    () => lsRead(STORAGE_KEYS.DAILY_LOG) || {},
    []
  );
  const debriefSnapshots = useMemo(
    () => lsRead(STORAGE_KEYS.DEBRIEF_SNAPSHOTS) || [],
    []
  );

  // Prefer approved plan baseline, fall back to calculated baseline.
  // Mirror Tab 7 re-hydration pattern: approved plan stores results: null in localStorage,
  // full results are in sessionStorage key psp_approved_plan_results (Tab 6 line ~2467).
  const baseline = useMemo(() => {
    const approved = lsRead(STORAGE_KEYS.APPROVED_PLAN);
    if (approved) {
      if (!approved.results) {
        try {
          const raw = sessionStorage.getItem('psp_approved_plan_results');
          if (raw) approved.results = JSON.parse(raw);
        } catch (_) { /* sessionStorage unavailable */ }
      }
      if (approved.results) return approved;
    }
    return getCalculatedBaseline();
  }, []);

  // -- Derived data ----------------------------------------------------------
  const opName = scenario?.operation_parameters?.operation_name || 'NOT SPECIFIED';
  // R2-09: operation period in Zulu for the after action review (same start date and time as Tab 2, Tab 5 and Tab 7)
  const opPeriod = scenario?.operation_parameters?.operation_start_date && Number(scenario?.operation_parameters?.duration_days) >= 1
    ? operationPeriodLabel(scenario.operation_parameters.operation_start_date, scenario.operation_parameters.operation_start_time_z || DEFAULT_START_TIME_Z, Number(scenario.operation_parameters.duration_days))
    : '';
  const opDays = scenario?.operation_parameters?.duration_days ?? 14;
  // R2-09: label of an operation day with its date and Zulu hour when the operation has a start date: 'D+3 (25 Oct 2026 06:00Z)'
  const dayLbl = (n) => {
    const op = scenario?.operation_parameters;
    try { return dayLabelZulu(op?.operation_start_date || '', op?.operation_start_time_z || DEFAULT_START_TIME_Z, Number(n)); } catch (_e) { return `D+${n}`; }
  };
  const latestSnap = debriefSnapshots.length > 0
    ? debriefSnapshots[debriefSnapshots.length - 1]
    : null;

  const baselineSeries = useMemo(
    () => extractDailySeries(baseline?.results?.by_day),
    [baseline]
  );
  const latestSeries = useMemo(
    () => extractDailySeries(latestSnap?.by_day),
    [latestSnap]
  );
  const actualSeries = useMemo(
    () => extractActualSeries(dailyLog),
    [dailyLog]
  );

  const baselineDoW  = baseline?.results?.by_corps?.total_dow ?? null;
  const latestDoW    = latestSnap?.by_corps?.total_dow ?? null;
  const baselineBlood = baseline?.results?.medical_requirements?.blood?.total_units ?? null;
  const latestBlood   = latestSnap?.medical_requirements?.blood?.total_units ?? null;

  const totalDecisions   = sessionHistory.length;
  const proactiveCount   = sessionHistory.filter(e => score3A(e) === 'PROACTIVE').length;
  const lateCount        = sessionHistory.filter(e => ['REACTIVE','LATE'].includes(score3A(e))).length;

  // -- Accordion expansion state ---------------------------------------------
  const [expanded, setExpanded] = useState({
    timeline: true,
    chart:    true,
    audit:    false,
  });
  const handleExpand = (key) => (_, isOpen) =>
    setExpanded(prev => ({ ...prev, [key]: isOpen }));

  // -- PDF Export ------------------------------------------------------------
  const [pdfBusy, setPdfBusy] = useState(false);

  function generateAARPdf() {
    setPdfBusy(true);
    try {
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

      // Cover
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('EXERCISE AFTER ACTION REVIEW', 105, 40, { align: 'center' });
      doc.setFontSize(14);
      doc.text('Medical Staff Officer Assessment', 105, 52, { align: 'center' });
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.text(`Operation: ${opName}`, 20, 72);
      let _coverY = 80;
      if (opPeriod) { doc.text(`Operation period: ${opPeriod}`, 20, _coverY); _coverY += 8; }
      doc.text(`Generated: ${new Date().toLocaleString()}`, 20, _coverY);
      doc.text(`Total Decisions Recorded: ${totalDecisions}`, 20, _coverY + 8);

      // Section 1 — Key Metrics
      doc.addPage();
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('1. EXERCISE KEY METRICS', 20, 25);
      const metricsRows = [
        ['Total DoW (baseline)', baselineDoW != null ? String(baselineDoW) : '--'],
        ['Total DoW (post-inject)', latestDoW  != null ? String(Math.round(latestDoW)) : 'N/A'],
        ['DoW Delta', (baselineDoW != null && latestDoW != null)
          ? fmtDelta(latestDoW - baselineDoW) : 'N/A'],
        ['Blood Req (baseline)', baselineBlood != null ? `${Math.round(baselineBlood)} units` : '--'],
        ['Blood Req (post-inject)', latestBlood != null ? `${Math.round(latestBlood)} units` : 'N/A'],
        ['Decisions made', String(totalDecisions)],
        ['Proactive decisions', `${proactiveCount} / ${totalDecisions}`],
        ['Late/reactive decisions', `${lateCount} / ${totalDecisions}`],
      ];
      autoTable(doc, {
        startY: 32,
        head: [['Metric', 'Value']],
        body: metricsRows,
        theme: 'grid',
        styles: { fontSize: 10 },
        headStyles: { fillColor: [25, 118, 210] },
      });

      // Section 2 — Decision timeline
      doc.addPage();
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('2. DECISION TIMELINE', 20, 25);
      const timelineRows = sessionHistory.map((e, i) => [
        String(i + 1),
        e.op_day != null ? dayLbl(e.op_day) : '--',
        e.planning_window ?? '--',
        e.type === 'COUNTERMEASURE' ? 'CM' : 'INJ',
        score3A(e),
        (e.inject_metas || []).map(m => m.label).join(', ') ||
          (e.countermeasure_metas || []).map(m => m.id).join(', ') || '--',
        e.delta?.total_dow != null ? fmtDelta(e.delta.total_dow) : '--',
        fmtTs(e.timestamp),
      ]);
      autoTable(doc, {
        startY: 32,
        head: [['#', 'D+N', 'Window', 'Type', '3A', 'Decisions', 'Delta DoW', 'Time']],
        body: timelineRows,
        theme: 'grid',
        styles: { fontSize: 8 },
        headStyles: { fillColor: [25, 118, 210] },
        columnStyles: {
          4: { fontStyle: 'bold' },
        },
      });

      doc.save(`AAR_${opName.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (_e) {
      // PDF generation error — non-critical
    } finally {
      setPdfBusy(false);
    }
  }

  // -- UI --------------------------------------------------------------------
  const hasData = sessionHistory.length > 0 || debriefSnapshots.length > 0 || actualSeries.length > 0;

  return (
    <Box>
      {/* Header */}
      <Alert severity="info" sx={{ mb: 2 }}>
        <AlertTitle>{t('tab8.title')}</AlertTitle>
        {t('tab8.subtitle')}
      </Alert>

      {/* ── Cross-student Decision Analysis (trainer only) ───────────────── */}
      {isTrainer && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: 'primary.main', borderWidth: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
            <GroupsIcon color="primary" />
            <Typography variant="subtitle1" fontWeight="bold" color="primary.main">
              {t('tab8.cross_student_title')}
            </Typography>
            <Chip label="TRAINER" size="small" color="primary" variant="outlined" sx={{ ml: 'auto !important' }} />
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 260 }}>
              <InputLabel>{t('tab8.select_exercise')}</InputLabel>
              <Select
                value={selectedExId}
                label={t('tab8.select_exercise')}
                onChange={e => setSelectedExId(e.target.value)}
              >
                <MenuItem value=""><em>{t('tab8.choose_exercise')}</em></MenuItem>
                {exercises.map(ex => (
                  <MenuItem key={ex.id} value={ex.id}>
                    {ex.name} <Chip label={ex.session_code} size="small" sx={{ ml: 1, fontSize: '0.65rem' }} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Tooltip title="Refresh exercises & decisions">
              <span>
                <IconButton
                  size="small"
                  onClick={() => { loadExerciseList(); loadDecisions(selectedExId); }}
                  disabled={aggregateLoading}
                >
                  {aggregateLoading
                    ? <CircularProgress size={18} />
                    : <RefreshIcon fontSize="small" />
                  }
                </IconButton>
              </span>
            </Tooltip>
            {injectDecisions.length > 0 && (
              <Typography variant="caption" color="text.secondary">
                {t('tab8.responses_across', { decisions: injectDecisions.length, injects: aggregated.length })}
              </Typography>
            )}
          </Stack>

          {aggregateError && (
            <Alert severity="error" sx={{ mb: 1.5 }}>{aggregateError}</Alert>
          )}

          {!selectedExId && !aggregateLoading && (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              {t('tab8.select_exercise_prompt')}
            </Typography>
          )}

          {selectedExId && !aggregateLoading && injectDecisions.length === 0 && !aggregateError && (
            <Alert severity="info">
              {t('tab8.no_inject_decisions')}
            </Alert>
          )}

          {/* Per-inject accordion breakdown */}
          {aggregated.map(group => {
            const display = INJECT_DISPLAY[group.inject_id] || { label: group.inject_label, color: 'default' };
            const isOpen  = !!aggExpanded[group.inject_id];
            return (
              <Accordion
                key={group.inject_id}
                expanded={isOpen}
                onChange={(_, open) => setAggExpanded(prev => ({ ...prev, [group.inject_id]: open }))}
                sx={{ mb: 0.5 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%', pr: 1 }}>
                    <Chip label={display.label} size="small" color={display.color} />
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {group.total} student{group.total !== 1 ? 's' : ''} responded
                    </Typography>
                    {/* Mini A/B/C/D bar */}
                    {['A','B','C','D'].map(opt => {
                      const n = group.decisionCounts[opt] || 0;
                      if (!n) return null;
                      const pct = Math.round((n / group.total) * 100);
                      return (
                        <Chip
                          key={opt}
                          label={`${opt}: ${n} (${pct}%)`}
                          size="small"
                          variant="outlined"
                          color={opt === 'A' ? 'success' : opt === 'B' ? 'primary' : opt === 'C' ? 'warning' : 'error'}
                          sx={{ fontSize: '0.65rem', height: 20 }}
                        />
                      );
                    })}
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  {/* Decision breakdown table */}
                  <TableContainer sx={{ mb: 1.5 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.72rem' } }}>
                          <TableCell>{t('tab8.col_option')}</TableCell>
                          <TableCell align="center">{t('tab8.col_count')}</TableCell>
                          <TableCell align="center">{t('tab8.col_pct')}</TableCell>
                          <TableCell>{t('tab8.col_students')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {['A','B','C','D'].map(opt => {
                          const n = group.decisionCounts[opt] || 0;
                          const pct = group.total > 0 ? Math.round((n / group.total) * 100) : 0;
                          const names = (group.studentsByDecision[opt] || []).join(', ');
                          return (
                            <TableRow key={opt} sx={{ opacity: n === 0 ? 0.35 : 1 }}>
                              <TableCell>
                                <Chip
                                  label={DECISION_LABELS[opt]}
                                  size="small"
                                  color={opt === 'A' ? 'success' : opt === 'B' ? 'primary' : opt === 'C' ? 'warning' : 'error'}
                                  variant={n > 0 ? 'filled' : 'outlined'}
                                  sx={{ fontSize: '0.7rem' }}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Typography variant="body2" fontWeight={n > 0 ? 700 : 400}>{n}</Typography>
                              </TableCell>
                              <TableCell align="center">
                                {/* Visual bar */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Box sx={{
                                    height: 8,
                                    width: `${pct * 0.8}px`,
                                    minWidth: n > 0 ? 4 : 0,
                                    bgcolor: opt === 'A' ? 'success.main' : opt === 'B' ? 'primary.main' : opt === 'C' ? 'warning.main' : 'error.main',
                                    borderRadius: 1,
                                    opacity: n > 0 ? 1 : 0,
                                  }} />
                                  <Typography variant="caption">{pct}%</Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                  {names || '—'}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Threat / Action / Confidence chips */}
                  <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                    {Object.entries(group.threatCounts).length > 0 && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 700, mb: 0.25 }}>
                          {t('tab8.threat_classification')}
                        </Typography>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                          {Object.entries(group.threatCounts).sort((a,b) => b[1]-a[1]).map(([v,n]) => (
                            <Chip key={v} label={`${v} ×${n}`} size="small" color={threatColor(v)} sx={{ fontSize: '0.65rem', height: 20 }} />
                          ))}
                        </Stack>
                      </Box>
                    )}
                    {Object.entries(group.actionCounts).length > 0 && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 700, mb: 0.25 }}>
                          {t('tab8.recommended_action')}
                        </Typography>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                          {Object.entries(group.actionCounts).sort((a,b) => b[1]-a[1]).map(([v,n]) => (
                            <Chip key={v} label={`${v} ×${n}`} size="small" color={actionColor(v)} sx={{ fontSize: '0.65rem', height: 20 }} />
                          ))}
                        </Stack>
                      </Box>
                    )}
                    {Object.entries(group.confidenceCounts).length > 0 && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 700, mb: 0.25 }}>
                          {t('tab8.confidence')}
                        </Typography>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                          {Object.entries(group.confidenceCounts).sort((a,b) => b[1]-a[1]).map(([v,n]) => (
                            <Chip key={v} label={`${v} ×${n}`} size="small" color={confidenceColor(v)} sx={{ fontSize: '0.65rem', height: 20 }} />
                          ))}
                        </Stack>
                      </Box>
                    )}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Paper>
      )}

      {!hasData && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('tab8.no_session_data')}{' '}{t('tab8.no_session_subtitle')}
        </Alert>
      )}

      {/* Summary metrics */}
      {hasData && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {/* Total decisions */}
          <Grid item xs={6} sm={3}>
            <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" color="primary" fontWeight="bold">
                {totalDecisions}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('tab8.decisions_recorded')}
              </Typography>
            </Paper>
          </Grid>

          {/* Proactive */}
          <Grid item xs={6} sm={3}>
            <Paper variant="outlined" sx={{ p: 2, textAlign: 'center',
              borderColor: proactiveCount > 0 ? 'success.main' : 'divider' }}>
              <Typography variant="h4" color="success.main" fontWeight="bold">
                {proactiveCount}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('tab8.proactive_decisions')}
              </Typography>
            </Paper>
          </Grid>

          {/* Late/reactive */}
          <Grid item xs={6} sm={3}>
            <Paper variant="outlined" sx={{ p: 2, textAlign: 'center',
              borderColor: lateCount > 0 ? 'error.main' : 'divider' }}>
              <Typography variant="h4" color={lateCount > 0 ? 'error.main' : 'text.primary'}
                fontWeight="bold">
                {lateCount}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('tab8.reactive_decisions')}
              </Typography>
            </Paper>
          </Grid>

          {/* DoW delta */}
          <Grid item xs={6} sm={3}>
            <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
              <Typography
                variant="h4"
                fontWeight="bold"
                color={
                  latestDoW == null || baselineDoW == null ? 'text.secondary'
                    : latestDoW > baselineDoW ? 'error.main' : 'success.main'
                }
              >
                {latestDoW != null && baselineDoW != null
                  ? fmtDelta(latestDoW - baselineDoW) : '--'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('tab8.delta_dow_label')}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Decision Timeline */}
      {sessionHistory.length > 0 && (
        <Accordion
          expanded={expanded.timeline}
          onChange={handleExpand('timeline')}
          sx={{ mb: 1 }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TimelineIcon color="primary" />
              <Typography variant="subtitle1" fontWeight="bold">
                {t('tab8.decision_timeline_entries', { count: sessionHistory.length })}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <Box sx={{ px: 2, pb: 2 }}>
              {sessionHistory.map((entry, idx) => {
                const winMeta   = PLANNING_WINDOW_META[entry.planning_window] ?? PLANNING_WINDOW_META.ORIENT;
                const scoreName = score3A(entry);
                const scoreMeta = SCORE_META[scoreName] ?? SCORE_META.TIMELY;
                const ScoreIcon = scoreMeta.icon;
                const isLast    = idx === sessionHistory.length - 1;
                return (
                  <Box key={entry.id || idx}>
                    <Box sx={{ display: 'flex', gap: 2, py: 1.5, alignItems: 'flex-start' }}>
                      {/* Left column — day/window */}
                      <Box sx={{ minWidth: 100 }}>
                        <Typography variant="body2" fontWeight="bold">
                          {entry.op_day != null ? dayLbl(entry.op_day) : '--'}
                        </Typography>
                        <Chip
                          size="small"
                          label={winMeta.label}
                          color={winMeta.muiColor}
                          sx={{ fontSize: '0.65rem', height: 18, mt: 0.25 }}
                        />
                      </Box>

                      {/* Centre — decision details */}
                      <Box sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                          <Chip
                            size="small"
                            label={entry.type === 'COUNTERMEASURE' ? 'CM' : 'INJ'}
                            color={entry.type === 'COUNTERMEASURE' ? 'success' : 'error'}
                            variant="outlined"
                            sx={{ fontSize: '0.65rem', height: 18 }}
                          />
                          {(entry.inject_metas || []).map(m => (
                            <Chip
                              key={m.id}
                              size="small"
                              label={`${m.label}: ${m.decision ?? '--'}`}
                              variant="outlined"
                              sx={{ fontSize: '0.65rem', height: 18 }}
                            />
                          ))}
                          {(entry.countermeasure_metas || []).map(m => (
                            <Chip
                              key={m.id}
                              size="small"
                              label={m.id}
                              color="success"
                              variant="outlined"
                              sx={{ fontSize: '0.65rem', height: 18 }}
                            />
                          ))}
                        </Box>
                        {entry.delta?.total_dow != null && (
                          <Typography variant="caption" color="text.secondary">
                            {t('tab8.delta_dow_caption', {
                              dow: fmtDelta(entry.delta.total_dow),
                              blood: entry.delta.blood_units != null ? fmtDelta(entry.delta.blood_units) : '--'
                            })}
                          </Typography>
                        )}
                      </Box>

                      {/* Right column — 3A score */}
                      <Box sx={{ textAlign: 'right', minWidth: 100 }}>
                        <Chip
                          size="small"
                          label={scoreName}
                          color={scoreMeta.muiColor}
                          icon={<ScoreIcon sx={{ fontSize: '0.85rem' }} />}
                          sx={{ fontSize: '0.7rem', height: 22 }}
                        />
                        <Typography
                          variant="caption"
                          display="block"
                          color="text.secondary"
                          sx={{ mt: 0.25 }}
                        >
                          {fmtTs(entry.timestamp)}
                        </Typography>
                      </Box>
                    </Box>
                    {!isLast && <Divider />}
                  </Box>
                );
              })}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Casualty Chart */}
      {(baselineSeries.length > 0 || actualSeries.length > 0) && (
        <Accordion
          expanded={expanded.chart}
          onChange={handleExpand('chart')}
          sx={{ mb: 1 }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShowChartIcon color="primary" />
              <Typography variant="subtitle1" fontWeight="bold">
                {t('tab8.chart_title')}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Alert severity="info" icon={<SchoolIcon fontSize="small" />} sx={{ mb: 2 }}>
              <Typography variant="body2">
                {t('tab8.chart_legend')}
              </Typography>
            </Alert>
            <CasualtyChart
              baselineSeries={baselineSeries}
              latestSeries={latestSeries}
              actualSeries={actualSeries}
              maxDay={opDays}
              startDate={scenario?.operation_parameters?.operation_start_date || ''}
              startTimeZ={scenario?.operation_parameters?.operation_start_time_z || DEFAULT_START_TIME_Z}
            />
          </AccordionDetails>
        </Accordion>
      )}

      {/* Decision Audit Table */}
      {sessionHistory.length > 0 && (
        <Accordion
          expanded={expanded.audit}
          onChange={handleExpand('audit')}
          sx={{ mb: 1 }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AssessmentIcon color="primary" />
              <Typography variant="subtitle1" fontWeight="bold">
                {t('tab8.audit_title')}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'primary.main' }}>
                    {['#', t('tab8.audit_col_day'), t('tab8.audit_col_window'), t('tab8.audit_col_type'), t('tab8.audit_col_score'), t('tab8.audit_col_decisions'), t('tab8.audit_col_delta_dow'), t('tab8.audit_col_delta_blood'), t('tab8.audit_col_time')].map(h => (
                      <TableCell key={h} sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem' }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sessionHistory.map((entry, idx) => {
                    const winMeta   = PLANNING_WINDOW_META[entry.planning_window] ?? PLANNING_WINDOW_META.ORIENT;
                    const scoreName = score3A(entry);
                    const scoreMeta = SCORE_META[scoreName] ?? SCORE_META.TIMELY;
                    const decLabel  =
                      (entry.inject_metas || []).map(m => `${m.label}:${m.decision}`).join(', ') ||
                      (entry.countermeasure_metas || []).map(m => m.id).join(', ') || '--';
                    return (
                      <TableRow
                        key={entry.id || idx}
                        sx={{ '&:nth-of-type(even)': { bgcolor: 'action.hover' } }}
                      >
                        <TableCell sx={{ fontSize: '0.75rem' }}>{idx + 1}</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
                          {entry.op_day != null ? dayLbl(entry.op_day) : '--'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={winMeta.label}
                            color={winMeta.muiColor}
                            sx={{ fontSize: '0.65rem', height: 18 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={entry.type === 'COUNTERMEASURE' ? 'CM' : 'INJ'}
                            color={entry.type === 'COUNTERMEASURE' ? 'success' : 'error'}
                            variant="outlined"
                            sx={{ fontSize: '0.65rem', height: 18 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={scoreName}
                            color={scoreMeta.muiColor}
                            sx={{ fontSize: '0.65rem', height: 18 }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.7rem', maxWidth: 200 }}>
                          {decLabel}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontSize: '0.75rem',
                            fontWeight: 'bold',
                            color: entry.delta?.total_dow == null ? 'text.secondary'
                              : entry.delta.total_dow > 0 ? 'error.main' : 'success.main',
                          }}
                        >
                          {entry.delta?.total_dow != null
                            ? fmtDelta(entry.delta.total_dow) : '--'}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontSize: '0.75rem',
                            color: entry.delta?.blood_units == null ? 'text.secondary'
                              : entry.delta.blood_units > 0 ? 'error.main' : 'success.main',
                          }}
                        >
                          {entry.delta?.blood_units != null
                            ? fmtDelta(entry.delta.blood_units) : '--'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                          {fmtTs(entry.timestamp)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>
      )}

      {/* 3A Framework legend */}
      {sessionHistory.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <SchoolIcon fontSize="small" color="primary" />
            <Typography variant="subtitle2" fontWeight="bold">
              {t('tab8.framework_title')}
            </Typography>
          </Box>
          <Grid container spacing={1}>
            {Object.entries(SCORE_META).map(([key, meta]) => {
              const ScoreIcon = meta.icon;
              const descriptions = {
                PROACTIVE: t('tab8.score_proactive_desc'),
                TIMELY:    t('tab8.score_timely_desc'),
                REACTIVE:  t('tab8.score_reactive_desc'),
                LATE:      t('tab8.score_late_desc'),
              };
              return (
                <Grid item xs={12} sm={6} key={key}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <ScoreIcon color={meta.muiColor} fontSize="small" sx={{ mt: 0.2 }} />
                    <Box>
                      <Typography variant="caption" fontWeight="bold" display="block">
                        {key}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {descriptions[key]}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Paper>
      )}

      {/* PDF Export */}
      {hasData && (
        <Box sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<GetAppIcon />}
            onClick={generateAARPdf}
            disabled={pdfBusy}
          >
            {pdfBusy ? t('tab8.generating_pdf') : t('tab8.export_pdf')}
          </Button>
        </Box>
      )}

      {/* Bottom Navigation */}
      <Divider sx={{ mb: 2 }} />
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 2 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => {
            onComplete && onComplete();
            onNavigate && onNavigate(6);
          }}
        >
          {t('tab8.back_to_tab7')}
        </Button>
      </Box>

      {/* Doctrinal reference */}
      <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Typography variant="caption" color="text.secondary" display="block">
          {t('tab8.doctrinal_refs')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
          <HealthAndSafetyIcon fontSize="small" color="disabled" />
          <Typography variant="caption" color="text.disabled">
            {t('tab8.version_label')}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
});

export default Tab8Debrief;
